import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { chartAxisTick, chartLegendStyle, chartTooltipProps } from '@/shared/lib/chart-theme';
import { brl, decimal } from '@/shared/lib/format';
import type { CampaignReport } from '../api/use-campaign-report';

const HEIGHT = 240;
const GRID = 'stroke-border/40';

/** "2026-10-24" → "24/10". */
function shortDay(key: string): string {
  const [, month, day] = key.split('-');
  return `${day}/${month}`;
}

function Empty({ children }: { children: string }) {
  return (
    <div className="flex items-center justify-center text-sm text-muted-foreground" style={{ height: HEIGHT }}>
      {children}
    </div>
  );
}

/** Entregas por dia: entregues × falhas. */
export function DispatchesChart({ daily }: { daily: CampaignReport['daily'] }) {
  const data = daily
    .filter((d) => d.delivered + d.failed > 0)
    .map((d) => ({ label: shortDay(d.date), Entregues: d.delivered, Falhas: d.failed }));
  if (data.length === 0) return <Empty>Nenhum disparo no período.</Empty>;

  return (
    <ResponsiveContainer width="100%" height={HEIGHT}>
      <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" className={GRID} vertical={false} />
        <XAxis dataKey="label" tick={chartAxisTick} tickLine={false} axisLine={false} />
        <YAxis tick={chartAxisTick} tickLine={false} axisLine={false} width={48} />
        <Tooltip formatter={(v: number) => decimal(v)} {...chartTooltipProps} />
        <Legend wrapperStyle={chartLegendStyle} />
        <Bar dataKey="Entregues" stackId="a" fill="hsl(var(--primary))" radius={[0, 0, 0, 0]} />
        <Bar dataKey="Falhas" stackId="a" fill="hsl(var(--destructive))" radius={[6, 6, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

/** Receita atribuída por dia (pedidos no tooltip). */
export function RevenueChart({ daily }: { daily: CampaignReport['daily'] }) {
  const data = daily
    .filter((d) => d.orders > 0)
    .map((d) => ({ label: shortDay(d.date), Receita: d.revenue, Pedidos: d.orders }));
  if (data.length === 0) return <Empty>Nenhuma compra atribuída no período.</Empty>;

  return (
    <ResponsiveContainer width="100%" height={HEIGHT}>
      <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" className={GRID} vertical={false} />
        <XAxis dataKey="label" tick={chartAxisTick} tickLine={false} axisLine={false} />
        <YAxis tick={chartAxisTick} tickLine={false} axisLine={false} width={64} tickFormatter={(v: number) => brl(v)} />
        <Tooltip
          formatter={(v: number, name: string) => [name === 'Receita' ? brl(v) : decimal(v), name]}
          {...chartTooltipProps}
        />
        <Area
          type="monotone"
          dataKey="Receita"
          stroke="hsl(var(--primary))"
          fill="hsl(var(--primary))"
          fillOpacity={0.15}
          strokeWidth={2}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

/** Quantos dias depois do disparo as compras acontecem. */
export function PurchaseLagChart({ lag }: { lag: CampaignReport['purchaseLag'] }) {
  if (lag.every((l) => l.orders === 0)) return <Empty>Nenhuma compra atribuída no período.</Empty>;
  const data = lag.map((l) => ({
    label: l.day === 0 ? 'No dia' : `${l.day}d`,
    Pedidos: l.orders,
    Clientes: l.buyers,
  }));

  return (
    <ResponsiveContainer width="100%" height={HEIGHT}>
      <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" className={GRID} vertical={false} />
        <XAxis dataKey="label" tick={chartAxisTick} tickLine={false} axisLine={false} />
        <YAxis tick={chartAxisTick} tickLine={false} axisLine={false} width={40} allowDecimals={false} />
        <Tooltip formatter={(v: number) => decimal(v)} {...chartTooltipProps} />
        <Legend wrapperStyle={chartLegendStyle} />
        <Bar dataKey="Pedidos" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
        <Bar dataKey="Clientes" fill="hsl(var(--muted-foreground))" radius={[6, 6, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

/** Funil: enviados → entregues → compraram. */
export function FunnelBars({ totals }: { totals: CampaignReport['totals'] }) {
  const steps = [
    { label: 'Envios', value: totals.sent, hint: 'tentativas de envio' },
    { label: 'Entregues', value: totals.delivered, hint: `${decimal(totals.deliveryRate, 1)}% dos envios` },
    { label: 'Clientes que compraram', value: totals.buyers, hint: `${decimal(totals.conversionRate, 1)}% de quem recebeu` },
  ];
  const max = Math.max(1, steps[0].value);

  return (
    <div className="space-y-4" style={{ minHeight: HEIGHT }}>
      {steps.map((step, i) => (
        <div key={step.label} className="space-y-1.5">
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <span className="font-medium">{step.label}</span>
            <span className="tabular-nums">
              <strong>{decimal(step.value)}</strong>
              <span className="ml-2 text-xs text-muted-foreground">{step.hint}</span>
            </span>
          </div>
          <div className="h-3 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary"
              style={{ width: `${Math.max(step.value > 0 ? 2 : 0, (step.value / max) * 100)}%`, opacity: 1 - i * 0.25 }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
